import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/extends";

/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import Downshift from 'downshift';
import { filter as fuzzySearch } from 'fuzzaldrin';
import Box from '../Box';
import MenuItem from '../utils/MenuItem';
import Icon from '../Icon';
import Flex from '../Flex';
import { InputControl, InputLabel, InputElement } from '../utils/Input';
import Tag from './Tag';
import { typedMemo } from '../../utils/helpers';
import Menu from '../utils/Menu';
import AbstractButton from '../AbstractButton';

var stateReducer = function stateReducer(state, changes) {
  switch (changes.type) {
    case Downshift.stateChangeTypes.keyDownEnter:
    case Downshift.stateChangeTypes.clickItem:
      // Whenever the user makes a selection, make sure to reset the input so that he can keep
      // searching for more. We also make sure to always highlight the first item
      return _extends({}, changes, {
        inputValue: '',
        highlightedIndex: state.highlightedIndex,
        isOpen: true
      });

    case Downshift.stateChangeTypes.blurInput:
      return _extends({}, changes, {
        inputValue: ''
      });

    default:
      return changes;
  }
};
/**
 * A simple MultiCombobox can be thought of as a typical `<select>` component. Whenerever you would
 * use a normal select, you should now pass the `<MultiCombobox>` component.
 */


function MultiCombobox(_ref) {
  var onChange = _ref.onChange,
      value = _ref.value,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'outline' : _ref$variant,
      items = _ref.items,
      _ref$disableItem = _ref.disableItem,
      disableItem = _ref$disableItem === void 0 ? function () {
    return false;
  } : _ref$disableItem,
      _ref$searchable = _ref.searchable,
      searchable = _ref$searchable === void 0 ? false : _ref$searchable,
      _ref$label = _ref.label,
      label = _ref$label === void 0 ? '' : _ref$label,
      hideLabel = _ref.hideLabel,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$placeholder = _ref.placeholder,
      placeholder = _ref$placeholder === void 0 ? '' : _ref$placeholder,
      _ref$itemToString = _ref.itemToString,
      _itemToString = _ref$itemToString === void 0 ? function (item) {
    return String(item);
  } : _ref$itemToString,
      _ref$allowAdditions = _ref.allowAdditions,
      allowAdditions = _ref$allowAdditions === void 0 ? false : _ref$allowAdditions,
      _ref$validateAddition = _ref.validateAddition,
      validateAddition = _ref$validateAddition === void 0 ? function () {
    return true;
  } : _ref$validateAddition,
      _ref$maxHeight = _ref.maxHeight,
      maxHeight = _ref$maxHeight === void 0 ? 300 : _ref$maxHeight,
      maxResults = _ref.maxResults,
      canClearAllAfter = _ref.canClearAllAfter,
      invalid = _ref.invalid,
      hidden = _ref.hidden,
      rest = _objectWithoutPropertiesLoose(_ref, ["onChange", "value", "variant", "items", "disableItem", "searchable", "label", "hideLabel", "disabled", "placeholder", "itemToString", "allowAdditions", "validateAddition", "maxHeight", "maxResults", "canClearAllAfter", "invalid", "hidden"]);

  var getVariant = React.useCallback(function (isOpen) {
    if (variant === 'solid') {
      return 'solid';
    }

    return isOpen ? 'solid' : 'outline';
  }, [variant]);

  var removeItem = function removeItem(item) {
    onChange(value.filter(function (i) {
      return i !== item;
    }));
  };

  var addSelectedItem = function addSelectedItem(item) {
    if (item !== null) {
      onChange([].concat(value, [item]));
    }
  };

  var clearSelectedItems = function clearSelectedItems() {
    onChange([]);
  };

  var itemsPt = hideLabel ? 3 : '19px';
  return /*#__PURE__*/React.createElement(Downshift, {
    stateReducer: stateReducer,
    onChange: addSelectedItem,
    selectedItem: null,
    itemToString: function itemToString(item) {
      return item ? _itemToString(item) : '';
    },
    initialInputValue: ""
  }, function (_ref2) {
    var getRootProps = _ref2.getRootProps,
        getInputProps = _ref2.getInputProps,
        getItemProps = _ref2.getItemProps,
        getMenuProps = _ref2.getMenuProps,
        getLabelProps = _ref2.getLabelProps,
        inputValue = _ref2.inputValue,
        isOpen = _ref2.isOpen,
        toggleMenu = _ref2.toggleMenu,
        openMenu = _ref2.openMenu,
        selectedItem = _ref2.selectedItem,
        selectItem = _ref2.selectItem;

    var processUserValue = function processUserValue(inputVal) {
      return (inputVal || '').trim();
    };

    var validateUserValue = function validateUserValue(inputVal) {
      return inputVal !== '' && validateAddition(inputVal, value);
    };

    var multiComboboxVariant = getVariant(isOpen); // If it's a multicombobox we DON'T WANT to include the results already selected and also
    // we want to make sure that the results get filtered by the search term of the user

    var nonSelectedItems = items.filter(function (item) {
      return !value.map(_itemToString).includes(_itemToString(item));
    }); // From the non-selected items, make sure to filter the ones that match the user's
    // search term. To do that we convert our items to their string representations

    var strResults = fuzzySearch(nonSelectedItems.map(_itemToString), inputValue || ''); // and then convert those strings back to the original shape of the items, while making
    // sure to only display a (potentially) limited number of them

    var results = items.filter(function (item) {
      return strResults.includes(_itemToString(item));
    }).slice(0, maxResults); // Only show the items that have not been selected
    // We add 2 types of additional data to the input that is going to be renders:
    // 1. A handler for the `Delete` button, so that you can delete tokens with a single key
    // 3. When the combobox is not searchable, we make the input "behave" like a div. We
    // still want an input though for placeholder, spacings, etc.

    var additionalInputProps = _extends({}, rest, !searchable && {
      cursor: 'pointer',
      onMouseDown: toggleMenu,
      readOnly: true,
      placeholder: !value.length ? placeholder : '',
      position: !value.length ? 'static' : 'absolute'
    }, searchable && {
      placeholder,
      mt: isOpen && value.length || isOpen && value.length && allowAdditions ? -4 : 0,
      position: isOpen || isOpen && value.length && allowAdditions ? 'static' : 'absolute'
    }, {
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      onFocus: openMenu,
      onKeyDown: function onKeyDown(event) {
        // Allow deletions of selections by pressing backspace
        if (event.key === 'Backspace' && !inputValue) {
          removeItem(value[value.length - 1]);
        } // Allow the user to add custom selections if both `searchable` and `allowAdditions`
        // have a truthy value


        if ((event.key === 'Enter' || event.key === ',') && allowAdditions) {
          event.preventDefault(); // By default validateAddition always returns true. Can be overriden by the user
          // for fine-grained addition

          var processedUserValue = processUserValue(inputValue);

          if (validateUserValue(processedUserValue)) {
            selectItem(processedUserValue, {
              inputValue: '',
              isOpen: true
            });
          }
        }
      },
      onBlur: function onBlur() {
        var processedUserValue = processUserValue(inputValue);

        if (allowAdditions && validateUserValue(processedUserValue)) {
          selectItem(processedUserValue, {
            inputValue: ''
          });
        }
      },
      onPaste: function onPaste(e) {
        // prevent it when we can only select values from the dropdown
        if (!allowAdditions) {
          return;
        } // Get clipboard data and split them based on newline and/or commas


        var clipboardData = e.clipboardData.getData('Text');
        var items = clipboardData.replace(/\r?\n/g, ',').split(',').map(processUserValue).filter(validateUserValue);

        if (items.length > 1) {
          // Prevent the text from actually being pasted to the underlying input
          e.preventDefault();
          e.stopPropagation(); // extend existing values with new ones

          onChange([].concat(value, items));
        }
      }
    });

    return /*#__PURE__*/React.createElement(Box, _extends({
      position: "relative"
    }, getRootProps()), /*#__PURE__*/React.createElement(Box, {
      position: "relative"
    }, /*#__PURE__*/React.createElement(InputControl, {
      invalid: invalid,
      disabled: disabled,
      variant: multiComboboxVariant,
      hidden: hidden
    }, value.length > 0 && /*#__PURE__*/React.createElement(Flex, {
      as: "ul",
      wrap: "wrap",
      pl: 3,
      pr: 10,
      pt: itemsPt,
      pb: "2px"
    }, value.map(function (selectedItem) {
      return /*#__PURE__*/React.createElement(Tag, {
        as: "li",
        key: _itemToString(selectedItem),
        m: 1,
        onRemove: function onRemove() {
          return removeItem(selectedItem);
        }
      }, _itemToString(selectedItem));
    })), canClearAllAfter && value.length >= canClearAllAfter && /*#__PURE__*/React.createElement(AbstractButton, {
      color: "teal-300",
      zIndex: 2,
      position: "absolute",
      bottom: 0,
      right: 18,
      mb: 1,
      onClick: clearSelectedItems,
      fontStyle: "italic",
      textDecoration: "underline",
      fontSize: "small"
    }, "Clear all"), /*#__PURE__*/React.createElement(InputElement, _extends({
      as: "input",
      type: "text",
      standalone: hideLabel
    }, getInputProps(additionalInputProps))), /*#__PURE__*/React.createElement(InputLabel, _extends({
      visuallyHidden: hideLabel,
      raised: !!value.length
    }, getLabelProps()), label)), items.length > 0 && /*#__PURE__*/React.createElement(Icon, {
      type: isOpen ? 'caret-up' : 'caret-down',
      position: "absolute",
      pointerEvents: "none",
      my: "auto",
      top: 0,
      bottom: 0,
      right: 4
    })), /*#__PURE__*/React.createElement(Menu, _extends({
      as: "ul",
      maxHeight: maxHeight,
      isOpen: isOpen && results.length > 0
    }, getMenuProps()), results.map(function (item) {
      return /*#__PURE__*/React.createElement(MenuItem, _extends({}, getItemProps({
        item,
        disabled: disableItem(item)
      }), {
        as: "li",
        listStyle: "none",
        key: _itemToString(item),
        selected: item === selectedItem
      }), _itemToString(item));
    })));
  });
}

export default typedMemo(MultiCombobox);